import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { planWeeks, weekStart } from '@actual-app/core/shared/mtxbudget';
import { parseAmount } from '@actual-app/core/shared/mtxbudget-completer';
import type { ToComplete } from '@actual-app/core/shared/mtxbudget-completer';

import { useAccounts } from '#hooks/useAccounts';

import { NotConfigured } from './QuinzainePage';
import { useWeekLabel, useWhenPaid } from './semaines';
import { hoursLabel, longDate, MtxPage, Section, useMoney } from './ui';
import { useMtxbudget } from './useMtxbudget';

const sectionLabel = {
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: 0.3,
  color: theme.pageTextSubdued,
  padding: '4px 4px 0',
  textTransform: 'uppercase' as const,
};

/**
 * The amounts the banks never send — a bill, the minimum of a statement, the
 * balance of an account not read this week — asked when they are needed. Each
 * one given replaces its estimate in the plan, for that charge only.
 */
export function ACompleterPage() {
  const { t } = useTranslation();
  const money = useMoney();
  const weekLabel = useWeekLabel();
  const whenPaid = useWhenPaid();
  const { data: accounts = [] } = useAccounts();
  const {
    config,
    isLoading,
    plan,
    today,
    charges,
    toComplete,
    provide,
    forget,
    putOff,
  } = useMtxbudget();
  const [editing, setEditing] = useState<string | null>(null);

  if (!config) {
    return (
      <MtxPage title={t('À compléter')} back="/quinzaine">
        {!isLoading && <NotConfigured />}
      </MtxPage>
    );
  }

  const current = planWeeks(plan, today, config.pay.minHoursPerWeek).find(
    w => w.offset === 0,
  );
  const accountName = (id?: string) =>
    accounts.find(account => account.id === id)?.name;

  // What was given and still lies ahead, to see, change or take back.
  const given = [
    ...Object.entries(config.providedBalances)
      .filter(([, p]) => p.on >= weekStart(today))
      .map(([accountId, p]) => ({
        id: `balance/${accountId}`,
        title: t('Solde du compte {{name}}', {
          name: accountName(accountId) ?? '',
        }),
        date: p.on,
        on: p.on,
        amount: p.amount,
      })),
    ...Object.entries(config.provided)
      .map(([id, p]) => {
        const charge = charges.find(c => c.id === id);
        const date = id.slice(id.lastIndexOf('/') + 1);
        return {
          id,
          title: charge?.name ?? id.slice(0, id.lastIndexOf('/')),
          date,
          on: p.on,
          amount: -p.amount,
        };
      })
      .filter(entry => entry.date >= today),
  ].sort((a, b) => a.date.localeCompare(b.date));

  const count = toComplete.length;
  const givenOn = (on: string) =>
    on === today
      ? t("fourni aujourd'hui")
      : t('fourni le {{date}}', { date: longDate(on) });

  return (
    <MtxPage
      title={t('À compléter')}
      back="/quinzaine"
      subtitle={
        count === 0
          ? t('Rien à compléter')
          : count === 1
            ? t('1 montant · les 14 prochains jours')
            : t('{{count}} montants · les 14 prochains jours', { count })
      }
    >
      <View style={{ gap: 12, paddingBottom: 20 }}>
        {current && (
          <Section style={{ padding: '12px 16px', gap: 4 }}>
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              {count > 0
                ? t('{{week}}, avec les montants estimés', {
                    week: weekLabel(0, today),
                  })
                : weekLabel(0, today)}
            </Text>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                columnGap: 8,
              }}
            >
              <Text
                style={{
                  whiteSpace: 'nowrap',
                  fontSize: 28,
                  fontWeight: 700,
                  color: theme.pageTextDark,
                  ...styles.tnum,
                }}
              >
                {hoursLabel(current.hours)}
              </Text>
              <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
                {t('se met à jour à chaque montant fourni')}
              </Text>
            </View>
          </Section>
        )}

        {count === 0 ? (
          <Section
            style={{
              alignItems: 'center',
              gap: 6,
              padding: '24px 16px',
              textAlign: 'center',
            }}
          >
            <Text style={{ fontWeight: 600, color: theme.pageTextDark }}>
              {t('Tous les montants des 14 prochains jours sont connus')}
            </Text>
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              {t(
                "Vos heures sont calculées sur des montants exacts. Le point d'entrée disparaît de Ma quinzaine.",
              )}
            </Text>
          </Section>
        ) : (
          <>
            <Text style={sectionLabel}>{t('À compléter')}</Text>
            {toComplete.map((item, index) => (
              <ItemCard
                key={item.id}
                item={item}
                first={index === 0}
                meta={
                  item.kind === 'balance'
                    ? t(
                        "La banque n'a rien envoyé pour ce compte depuis dimanche.",
                      )
                    : [
                        item.statement
                          ? t('dû {{when}}', {
                              when: whenPaid(item.date, today),
                            })
                          : t('à payer {{when}}', {
                              when: whenPaid(item.date, today),
                            }),
                        accountName(item.account),
                      ]
                        .filter(Boolean)
                        .join(' · ')
                }
                onProvide={cents => provide(item, cents)}
                onLater={() => putOff(item)}
              />
            ))}
          </>
        )}

        {given.length > 0 && (
          <>
            <Text style={{ ...sectionLabel, paddingTop: 8 }}>
              {t('Déjà fournis')}
            </Text>
            <Section>
              {given.map((entry, index) => {
                const last = index === given.length - 1;
                return (
                  <View
                    key={entry.id}
                    style={{
                      borderBottom: last
                        ? undefined
                        : `1px solid ${theme.tableBorder}`,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 16px',
                      }}
                    >
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          style={{ fontWeight: 500, color: theme.pageTextDark }}
                        >
                          {entry.title}
                        </Text>
                        <Text
                          style={{ fontSize: 12, color: theme.pageTextSubdued }}
                        >
                          {`${longDate(entry.date)} · ${givenOn(entry.on)}`}
                        </Text>
                      </View>
                      <Text
                        style={{
                          fontWeight: 600,
                          color: theme.pageTextDark,
                          whiteSpace: 'nowrap',
                          ...styles.tnum,
                        }}
                      >
                        {money(entry.amount)}
                      </Text>
                    </View>
                    {editing === entry.id ? (
                      <View style={{ padding: '0 16px 12px' }}>
                        <AmountInput
                          id={`modifier-${entry.id}`}
                          label={t('Nouveau montant')}
                          placeholder={money(entry.amount).replace(/\s*\$/, '')}
                          primary
                          onConfirm={cents => {
                            if (entry.id.startsWith('balance/')) {
                              provide(
                                {
                                  kind: 'balance',
                                  id: entry.id,
                                  accountId: entry.id.slice('balance/'.length),
                                  name: entry.title,
                                  estimate: entry.amount,
                                },
                                cents,
                              );
                            } else {
                              provide(
                                {
                                  kind: 'amount',
                                  id: entry.id,
                                  name: entry.title,
                                  date: entry.date,
                                  estimate: entry.amount,
                                  statement: false,
                                },
                                cents,
                              );
                            }
                            setEditing(null);
                          }}
                        />
                      </View>
                    ) : (
                      <View
                        style={{
                          flexDirection: 'row',
                          gap: 8,
                          padding: '0 16px 12px',
                        }}
                      >
                        <Button
                          onPress={() => setEditing(entry.id)}
                          style={{ flex: 1, minWidth: 0, minHeight: 44 }}
                        >
                          <Trans>Modifier</Trans>
                        </Button>
                        <Button
                          onPress={() => forget(entry.id)}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            minHeight: 44,
                            color: theme.errorText,
                          }}
                        >
                          {t("Revenir à l'estimé")}
                        </Button>
                      </View>
                    )}
                  </View>
                );
              })}
            </Section>
          </>
        )}

        <Text
          style={{
            fontSize: 12,
            color: theme.pageTextSubdued,
            padding: '0 4px',
          }}
        >
          {t(
            "Un montant fourni vaut pour cette échéance seulement ; le mois suivant reprend l'estimé, et vous sera redemandé.",
          )}
        </Text>
      </View>
    </MtxPage>
  );
}

function ItemCard({
  item,
  first,
  meta,
  onProvide,
  onLater,
}: {
  item: ToComplete;
  first: boolean;
  meta: string;
  onProvide: (cents: number) => void;
  onLater: () => void;
}) {
  const { t } = useTranslation();
  const money = useMoney();
  const isBalance = item.kind === 'balance';
  const title = isBalance
    ? t('Solde du compte {{name}}', { name: item.name })
    : item.name;
  const why = isBalance
    ? t(
        "Recopiez le solde affiché dans l'application de la banque. Mettez un moins s'il est en découvert.",
      )
    : item.statement
      ? t('La banque ne transmet pas le minimum du relevé.')
      : t("Le montant change d'un mois à l'autre.");
  const label = isBalance
    ? t("Solde aujourd'hui")
    : item.statement
      ? t('Montant du relevé')
      : t('Montant de la facture');
  return (
    <Section
      style={{
        gap: 10,
        padding: '14px 16px',
        border: `1px solid ${first ? theme.buttonPrimaryBackground : theme.tableBorder}`,
      }}
    >
      <View>
        <Text style={{ fontWeight: 600, color: theme.pageTextDark }}>
          {title}
        </Text>
        <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
          {meta}
        </Text>
        <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
          {why}
        </Text>
      </View>
      <AmountInput
        id={`montant-${item.id}`}
        label={label}
        placeholder={money(item.estimate).replace(/\s*\$/, '')}
        primary={first}
        onConfirm={onProvide}
      />
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
          {isBalance
            ? t('Dernier solde connu : {{amount}}', {
                amount: money(item.estimate),
              })
            : t('Estimé : environ {{amount}}', {
                amount: money(item.estimate),
              })}
        </Text>
        <Button
          variant="bare"
          onPress={onLater}
          style={{
            minHeight: 44,
            padding: '0 4px',
            fontSize: 13,
            fontWeight: 600,
            color: theme.pageTextLink,
          }}
        >
          <Trans>Plus tard</Trans>
        </Button>
      </View>
    </Section>
  );
}

/** An amount in dollars, refused with a word when it is not one. */
function AmountInput({
  id,
  label,
  placeholder,
  primary,
  onConfirm,
}: {
  id: string;
  label: string;
  placeholder: string;
  primary: boolean;
  onConfirm: (cents: number) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const cents = parseAmount(text);
  const invalid = text.trim() !== '' && cents === null;
  return (
    <View style={{ gap: 6 }}>
      <label htmlFor={id} style={{ fontSize: 12, color: theme.pageText }}>
        {label}
      </label>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <View
          style={{
            flex: 1,
            minWidth: 0,
            flexDirection: 'row',
            alignItems: 'center',
            minHeight: 48,
            padding: '0 12px',
            borderRadius: 4,
            border: `1px solid ${invalid ? theme.errorText : primary ? theme.buttonPrimaryBackground : theme.tableBorder}`,
            backgroundColor: theme.tableBackground,
          }}
        >
          <input
            id={id}
            type="text"
            inputMode="decimal"
            value={text}
            placeholder={placeholder}
            aria-invalid={invalid}
            aria-describedby={invalid ? `${id}-erreur` : undefined}
            onChange={event => setText(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && cents !== null) {
                onConfirm(cents);
                setText('');
              }
            }}
            style={{
              flex: 1,
              minWidth: 0,
              border: 0,
              background: 'transparent',
              color: theme.pageTextDark,
              font: 'inherit',
              fontSize: 18,
              textAlign: 'right',
              outline: 'none',
            }}
          />
          <Text style={{ paddingLeft: 6, color: theme.pageTextSubdued }}>
            $
          </Text>
        </View>
        <Button
          variant={primary ? 'primary' : 'normal'}
          isDisabled={cents === null}
          onPress={() => {
            if (cents !== null) {
              onConfirm(cents);
              setText('');
            }
          }}
          style={{ minHeight: 48, padding: '0 16px', fontWeight: 600 }}
        >
          <Trans>Confirmer</Trans>
        </Button>
      </View>
      {invalid && (
        <Text
          id={`${id}-erreur`}
          style={{ fontSize: 13, color: theme.errorText }}
        >
          {t('Entrez un montant en dollars, par exemple {{example}}.', {
            example: placeholder || '68,00',
          })}
        </Text>
      )}
    </View>
  );
}
